"use client"

import { createContext, useContext, useState } from "react"

const LanguageContext = createContext()

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider")
  }
  return context
}

const translations = {
  en: {
    // Header
    home: "Home",
    apartments: "Apartments",
    about: "About",
    contact: "Contact",

    // Not Found Page
    pageNotFound: "Page Not Found",
    pageNotFoundMessage: "The page you are looking for doesn't exist or has been moved.",
    backToHome: "Back to Home",

    // Hero
    heroTitle: "Live the City in Style",
    heroSubtitle: "Handpicked luxury apartments for your perfect getaway",
    

    // Filter
    filterTitle: "Find Your Perfect Stay",
    priceRange: "Price Range",
    guests: "Number of Guests",
    anyPrice: "Any Price",
    anyGuests: "Any Guests",
    resetFilters: "Reset Filters",

    // Apartments
    perNight: "per night",
    sqm: "sqm",
    viewDetails: "View Details",
    noApartmentsFound: "No apartments found",
    tryDifferentFilters: "Try different filters",
    
    // Apartment Details
    backToApartments: "Back to Apartments",
    description: "Description",
    amenities: "Amenities",
    bookNow: "Book Now",
    contactUs: "Contact Us",
    bedrooms: "Bedrooms",
    bathrooms: "Bathrooms",
    guestsCount: "Guests",
    loading: "Loading...",
    locationTitle: "Location",
    
    // Availability Calendar
    availabilityCalendar: "Availability Calendar",
    available: "Available",
    booked: "Booked",
    refreshAvailability: "Refresh Availability",
    loadingAvailability: "Loading availability data...",
    errorLoadingAvailability: "Error loading availability data",
    retry: "Retry",

    // About
    aboutTitle: "About Gold House Apartments",
    aboutText1: "For over 8 years, Gold House Apartments has specialized in premium short-term rentals located in the heart of the city. We work in collaboration with leading platforms such as Airbnb, Booking, and others to provide our guests with a seamless, high-standard hospitality experience.",
    aboutText2: "Our apartments are carefully designed and fully equipped to offer maximum comfort, privacy, and cleanliness — all within walking distance of the city's most vibrant areas, attractions, and public transport.",
    aboutText3: "At Gold House, we believe that personal service makes all the difference. That's why we offer full-time availability, fast responses, and attentive care to every guest, every time.",
    visionTitle: "Our Vision",
    visionText: "To continue growing as a trusted name in the short-term rental industry, expanding into new locations — while maintaining our core values: exceptional service, central locations, and uncompromising quality.",

    // Contact
    contactTitle: "Contact Us",
    contactSubtitle: "Get in touch for bookings and inquiries",
    name: "Name",
    email: "Email",
    phone: "Phone Number",
    message: "Message",
    sendMessage: "Send Message",
    sending: "Sending...",
    sent: "Sent",
    error: "Error",
    messageSent: "Your message has been sent successfully!",
    messageError: "There was an error sending your message. Please try again.",
    address: "14 Beer Sheva Street, Nachlaot, Jerusalem",
    emailClickMessage: "Click here to send us a message",
    phoneClickMessage: "Click here to call us",
    whatsappClickMessage: "Click here to send us a WhatsApp message",

    // Map
    mapTitle: "Our Location",

    // Footer
    followUs: "Follow Us",
    contactInfo: "Contact Information",
    quickLinks: "Quick Links",
    privacyPolicy: "Privacy Policy",
    termsOfUse: "Terms of Use",
    allRightsReserved: "All rights reserved.",

    // Coming Soon
    comingSoon: "Coming Soon",
    comingSoonMessage: "This feature is currently under development and will be available soon.",
  },
  fr: {
    // Header
    home: "Accueil",
    apartments: "Appartements",
    about: "À Propos",
    contact: "Contact",

    // Not Found Page
    pageNotFound: "Page Non Trouvée",
    pageNotFoundMessage: "La page que vous recherchez n'existe pas ou a été déplacée.",
    backToHome: "Retour à l'Accueil",

    // Hero
    heroTitle: "Vivre la ville avec style",
    heroSubtitle: "Appartements de luxe soigneusement sélectionnés pour un séjour parfait",

    // Filter
    filterTitle: "Trouvez Votre Séjour Parfait",
    priceRange: "Gamme de Prix",
    guests: "Nombre d'Invités",
    anyPrice: "Tout Prix",
    anyGuests: "Tout Invité",
    resetFilters: "Réinitialiser les Filtres",

    // Apartments
    perNight: "par nuit",
    sqm: "m²",
    viewDetails: "Voir les Détails",
    noApartmentsFound: "Aucun appartement trouvé",
    tryDifferentFilters: "Essayez différents filtres",
    
    // Apartment Details
    backToApartments: "Retour aux Appartements",
    description: "Description",
    amenities: "Équipements",
    bookNow: "Réserver Maintenant",
    contactUs: "Contactez-Nous",
    bedrooms: "Chambres",
    bathrooms: "Salles de Bain",
    guestsCount: "Invités",
    loading: "Chargement...",
    locationTitle: "Emplacement",
    
    // Availability Calendar
    availabilityCalendar: "Calendrier de Disponibilité",
    available: "Disponible",
    booked: "Réservé",
    refreshAvailability: "Actualiser les Disponibilités",
    loadingAvailability: "Chargement des disponibilités...",
    errorLoadingAvailability: "Erreur lors du chargement des disponibilités",
    retry: "Réessayer",

    // About
    aboutTitle: "À Propos de Gold House Apartments",
    aboutText1: "Depuis plus de 8 ans, Gold House Apartments s'est spécialisé dans les locations de courte durée haut de gamme situées au cœur de la ville. Nous travaillons en collaboration avec des plateformes de premier plan telles que Airbnb, Booking et d'autres pour offrir à nos clients une expérience d'hospitalité fluide et de haute qualité.",
    aboutText2: "Nos appartements sont soigneusement conçus et entièrement équipés pour offrir un maximum de confort, d'intimité et de propreté — tous à distance de marche des quartiers les plus animés de la ville, des attractions et des transports en commun.",
    aboutText3: "Chez Gold House, nous croyons que le service personnalisé fait toute la différence. C'est pourquoi nous offrons une disponibilité à temps plein, des réponses rapides et une attention particulière à chaque invité, à chaque fois.",
    visionTitle: "Notre Vision",
    visionText: "Continuer à grandir en tant que nom de confiance dans l'industrie de la location à court terme, en nous étendant vers de nouveaux emplacements — tout en maintenant nos valeurs fondamentales : service exceptionnel, emplacements centraux et qualité sans compromis.",

    // Contact
    contactTitle: "Contactez-Nous",
    contactSubtitle: "Contactez-nous pour réservations et demandes",
    name: "Nom",
    email: "Email",
    phone: "Numéro de Téléphone",
    message: "Message",
    sendMessage: "Envoyer Message",
    sending: "Envoi en cours...",
    sent: "Envoyé",
    error: "Erreur",
    messageSent: "Votre message a été envoyé avec succès!",
    messageError: "Une erreur s'est produite lors de l'envoi de votre message. Veuillez réessayer.",
    address: "14 rue Beer Sheva, Nachlaot, Jerusalem",
    emailClickMessage: "Cliquez ici pour nous envoyer un message",
    phoneClickMessage: "Cliquez ici pour nous appeler",
    whatsappClickMessage: "Cliquez ici pour nous envoyer un message WhatsApp",

    // Map
    mapTitle: "Notre Emplacement",

    // Footer
    followUs: "Suivez-Nous",
    contactInfo: "Informations de Contact",
    quickLinks: "Liens Rapides",
    privacyPolicy: "Politique de Confidentialité",
    termsOfUse: "Conditions d'utilisation",
    allRightsReserved: "Tous droits réservés.",

    // Coming Soon
    comingSoon: "Bientôt disponible",
    comingSoonMessage: "Cette fonctionnalité est actuellement en développement et sera disponible bientôt.",
  },
  es: {
    // Header
    home: "Inicio",
    apartments: "Apartamentos",
    about: "Acerca",
    contact: "Contacto",

    // Not Found Page
    pageNotFound: "Página No Encontrada",
    pageNotFoundMessage: "La página que estás buscando no existe o ha sido movida.",
    backToHome: "Volver al Inicio",

    // Hero
    heroTitle: "Vive la ciudad con estilo",
    heroSubtitle: "Apartamentos de lujo seleccionados para una escapada perfecta",

    // Filter
    filterTitle: "Encuentra Tu Estancia Perfecta",
    priceRange: "Rango de Precios",
    guests: "Número de Huéspedes",
    anyPrice: "Cualquier Precio",
    anyGuests: "Cualquier Huésped",
    resetFilters: "Restablecer Filtros",

    // Apartments
    perNight: "por noche",
    sqm: "m²",
    viewDetails: "Ver Detalles",
    noApartmentsFound: "No se encontraron apartamentos",
    tryDifferentFilters: "Prueba con diferentes filtros",
    
    // Apartment Details
    backToApartments: "Volver a Apartamentos",
    description: "Descripción",
    amenities: "Comodidades",
    bookNow: "Reservar Ahora",
    contactUs: "Contáctanos",
    bedrooms: "Dormitorios",
    bathrooms: "Baños",
    guestsCount: "Huéspedes",
    loading: "Cargando...",
    locationTitle: "Ubicación",
    
    // Availability Calendar
    availabilityCalendar: "Calendario de Disponibilidad",
    available: "Disponible",
    booked: "Reservado",
    refreshAvailability: "Actualizar Disponibilidad",
    loadingAvailability: "Cargando datos de disponibilidad...",
    errorLoadingAvailability: "Error al cargar datos de disponibilidad",
    retry: "Reintentar",

    // About
    aboutTitle: "Acerca de Gold House Apartments",
    aboutText1: "Durante más de 8 años, Gold House Apartments se ha especializado en alquileres premium a corto plazo ubicados en el corazón de la ciudad. Trabajamos en colaboración con plataformas líderes como Airbnb, Booking y otras para proporcionar a nuestros huéspedes una experiencia de hospitalidad perfecta y de alto nivel.",
    aboutText2: "Nuestros apartamentos están cuidadosamente diseñados y totalmente equipados para ofrecer el máximo confort, privacidad y limpieza — todos a poca distancia a pie de las áreas más vibrantes de la ciudad, atracciones y transporte público.",
    aboutText3: "En Gold House, creemos que el servicio personal marca la diferencia. Por eso ofrecemos disponibilidad a tiempo completo, respuestas rápidas y atención cuidadosa a cada huésped, cada vez.",
    visionTitle: "Nuestra Visión",
    visionText: "Continuar creciendo como un nombre de confianza en la industria de alquiler a corto plazo, expandiéndonos a nuevas ubicaciones — mientras mantenemos nuestros valores fundamentales: servicio excepcional, ubicaciones centrales y calidad sin compromisos.",

    // Contact
    contactTitle: "Contáctanos",
    contactSubtitle: "Ponte en contacto para reservas e consultas",
    name: "Nombre",
    email: "Email",
    phone: "Número de Teléfono",
    message: "Mensaje",
    sendMessage: "Enviar Mensaje",
    sending: "Enviando...",
    sent: "Enviado",
    error: "Error",
    messageSent: "¡Tu mensaje ha sido enviado con éxito!",
    messageError: "Hubo un error al enviar tu mensaje. Por favor, inténtalo de nuevo.",
    address: "Calle Beer Sheva 14, Nachlaot, Jerusalén",
    emailClickMessage: "Haz clic aquí para enviarnos un mensaje",
    phoneClickMessage: "Haz clic aquí para llamarnos",
    whatsappClickMessage: "Haz clic aquí para enviarnos un mensaje por WhatsApp",

    // Map
    mapTitle: "Nuestra Ubicación",

    // Footer
    followUs: "Síguenos",
    contactInfo: "Información de Contacto",
    quickLinks: "Enlaces Rápidos",
    privacyPolicy: "Política de Privacidad",
    termsOfUse: "Términos de Uso",
    allRightsReserved: "Todos los derechos reservados.",

    // Coming Soon
    comingSoon: "Próximamente",
    comingSoonMessage: "Esta funcionalidad está actualmente en desarrollo y estará disponible pronto.",
  },
  he: {
    // Header
    home: "בית",
    apartments: "הדירות שלנו",
    about: "אודותינו",
    contact: "צור קשר",

    // Not Found Page
    pageNotFound: "הדף לא נמצא",
    pageNotFoundMessage: "הדף שאתה מחפש אינו קיים או הועבר למקום אחר.",
    backToHome: "חזרה לדף הבית",

    // Hero
    heroTitle: "גלו את העיר עם יוקרה",
    heroSubtitle: "דירות יוקרה בלב העיר לחוויה בלתי נשכחת",

    // Filter
    filterTitle: "מצא את הדירה המושלמת בשבילך",
    priceRange: "טווח מחירים",
    guests: "מספר אורחים",
    anyPrice: "הכל",
    anyGuests: "הכל",
    resetFilters: "איפוס סינון",

    // Apartments
    perNight: "לכל לילה",
    sqm: "מ\"ר",
    viewDetails: "הצג פרטים",
    noApartmentsFound: "לא נמצאו דירות",
    tryDifferentFilters: "נסה סינון אחר",
    
    // Apartment Details
    backToApartments: "חזרה לדף הבית",
    description: "תיאור",
    amenities: " שונות",
    bookNow:  "הזמן עכשיו",
    contactUs: "צור קשר",
    bedrooms: "חדרי שינה",
    bathrooms: "מקלחות",
    guestsCount: "אורחים",
    loading: "טוען נתונים...",
    locationTitle: "מיקום",
    
    // Availability Calendar
    availabilityCalendar: "לוח זמינות",
    available: "פנוי",
    booked: "תפוס",
    refreshAvailability: "רענן זמינות",
    loadingAvailability: "טוען נתוני זמינות...",
    errorLoadingAvailability: "שגיאה בטעינת נתוני זמינות",
    retry: "נסה שוב",
    
    // About
    aboutTitle: "אודותינו",
    aboutText1: "כבר למעלה מ־8 שנים, Gold House Apartments מתמחה בדירות יוקרה להשכרה לטווח קצר, בלב העיר. אנו פועלים בשיתוף פעולה עם פלטפורמות מובילות כגון Airbnb, Booking ואחרות – במטרה להעניק לאורחינו חוויית אירוח חלקה, איכותית וברמה הגבוהה ביותר.",
    aboutText2: "הדירות שלנו מעוצבות בקפידה ומאובזרות במלואן, כדי להעניק חוויית אירוח מקסימלית של נוחות, פרטיות וניקיון — והכול במרחק הליכה מהאזורים התוססים ביותר של העיר, מאתרי הבילוי המרכזיים ומהתחבורה הציבורית.",
    aboutText3: "ב-Gold House אנו מאמינים ששירות אישי הוא המפתח לחוויית אירוח יוצאת דופן. בדיוק בגלל זה אנחנו מקפידים להיות זמינים תמיד, להגיב במהירות ולדאוג לכל פרט – כדי שכל אורח ירגיש בבית, בכל פעם מחדש.",
    visionTitle: "החזון שלנו",
    visionText: "להמשיך לצמוח כמותג אמין ומוביל בתחום ההשכרות לטווח קצר, תוך התרחבות ליעדים חדשים — וכל זאת תוך שמירה על ערכי הליבה שלנו: שירות יוצא דופן, מיקומים מרכזיים ואיכות ללא פשרות.",
    
    // Contact
    contactTitle: "צור קשר",
    contactSubtitle: "לכל שאלה - צרו איתנו קשר",
    name: "שם",
    email: "דואר אלקטרוני",
    phone: "טלפון",
    message: "הודעה",
    sendMessage: "שלח הודעה",
    sending: "שולח...",
    sent: "נשלח",
    error: "שגיאה",
    messageSent: "ההודעה נשלחה בהצלחה!",
    messageError: "שגיאה בשליחת ההודעה. אנא נסה שוב.",
    address: "רחוב באר שבע 14, ירושלים",
    emailClickMessage: "לחץ כאן לשליחת הודעה",
    phoneClickMessage: "לחץ כאן לחיוג",
    whatsappClickMessage: "לחץ כאן לשליחת הודעה בוואטסאפ",
    
    // Map
    mapTitle: "מיקום",
    
    // Footer
    followUs: "עקבו אחרינו",
    contactInfo: "פרטי יצירת קשר",
    quickLinks: "קישורים מהירים",
    privacyPolicy: "מדיניות פרטיות",
    termsOfUse: "תנאי שימוש",
    allRightsReserved: "כל הזכויות שמורות.",

    // Blog
    blogTitle: "בלוג תיירות",
    blogSubtitle: "",
    blogReadMore: "",
    blogViewAll: "",
    blogViewAllText: "",
    blogViewAllLink: "",

    // Coming Soon
    comingSoon: "בהרצה",
    comingSoonMessage: "אזור זה עדיין בפיתוח ויהיה זמין בקרוב.",
  },
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState("en")

  const t = (key) => {
    return translations[language][key] || key
  }

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>
}
